"use client";
import React from "react";

import { Box, BoxSx, Target } from "../../foundations";

export type SwitchProps = React.ComponentProps<typeof Target> & {
  disabled?: boolean;
  label?: string;
  onClick?: (event: React.MouseEvent) => void;
  selected?: boolean;
};

export const Switch: React.FC<SwitchProps> = ({
  disabled = false,
  label,
  onClick,
  sx,
  selected = false
}: SwitchProps) => {
  const handleToggle = (event: React.MouseEvent) => {
    if (!disabled) {
      onClick?.(event);
    }
  };

  const layoutStyles: BoxSx = {
    display: "inline-flex",
    justifyContent: "center",
    minHeight: "spacing-6"
  };

  const appearanceStyles: BoxSx = {
    borderRadius: "radius-round",
    borderStyle: "solid",
    borderWidth: 0,
    transition: "transition-color-medium",
    pointerEvents: disabled ? "none" : "auto"
  };

  const colorStyles = React.useMemo<BoxSx>(
    () => ({
      backgroundColor: selected
        ? "switch-surface-selected-enabled"
        : "switch-surface-notselected-enabled",
      ":hover": {
        backgroundColor: selected
          ? "switch-surface-selected-hover"
          : "switch-surface-notselected-hover"
      },
      ":active": {
        backgroundColor: selected
          ? "switch-surface-selected-click"
          : "switch-surface-notselected-click"
      },
      ":focus-visible": {
        outlineWidth: "spacing-1",
        outlineStyle: "solid",
        outlineColor: "focus",
        outlineOffset: "2px"
      }
    }),
    [selected]
  );

  const knobContainerStyles: BoxSx = {
    alignItems: "center",
    justifyContent: "center",
    marginX: "spacing-1",
    minWidth: "spacing-8",
    position: "relative"
  };

  const knobStyles: BoxSx = {
    backgroundColor: "switch-onsurface-color",
    borderRadius: "radius-round",
    boxShadow: disabled ? "shadow-none" : "shadow-100",
    height: "spacing-5",
    left: selected ? "spacing-5" : "spacing-none",
    minWidth: "spacing-5",
    position: "absolute",
    top: "spacing-1",
    transition: () => "left 0.2s ease"
  };

  const disabledStyles = React.useMemo<BoxSx>(() => {
    if (disabled) {
      return {
        backgroundColor: "switch-surface-color-disabled",
        cursor: "not-allowed",
        ":hover": {
          backgroundColor: "switch-surface-color-disabled"
        },
        ":active": {
          backgroundColor: "switch-surface-color-disabled"
        }
      };
    }
    return {};
  }, [disabled]);

  const containerStyles: BoxSx = {
    alignItems: "center",
    color: disabled ? "text-input-disabled" : "text-default",
    display: "inline-flex",
    fontSize: "font-size-4",
    gap: "spacing-3",
    marginLeft: "spacing-2",
    cursor: disabled ? "not-allowed" : "pointer"
  };

  return (
    <Box
      role="group"
      sx={{
        ...containerStyles,
        ...sx
      }}
      onClick={handleToggle} // Toggle when clicking the container or label
    >
      {/* Switch Button */}
      <Box
        as="button"
        role="switch"
        aria-checked={selected}
        aria-disabled={disabled}
        aria-labelledby={
          label ? `${label.replace(/\s+/g, "-")}-label` : "switch-label"
        }
        disabled={disabled}
        sx={{
          ...layoutStyles,
          ...appearanceStyles,
          ...colorStyles,
          ...disabledStyles
        }}
      >
        <Box sx={knobContainerStyles}>
          <Box sx={knobStyles}></Box>
        </Box>
      </Box>

      {/* Label */}
      {label && (
        <Box
          as="span"
          id={`${label.replace(/\s+/g, "-")}-label`}
          sx={{ cursor: disabled ? "not-allowed" : "pointer" }} // Match cursor to disabled state
        >
          {label}
        </Box>
      )}
    </Box>
  );
};
